import { gql } from '@apollo/client';

export const GET_USER_DETAILS = gql`
  query getUser {
    user {
      id
      lName
      created
      email
      fName
      modified
      status
      access {
        groups
        scopes
      }
      organisations {
        created
        userId
        orgId
        modified
        name
        status
        access {
          groups
          scopes
        }
        org {
          defaultRoute
          legalEntity {
            address {
              country
            }
          }
          plan
        }
      }
    }
  }
`;

export const GET_USERSTATUSLIST = gql`
  query getUserStatusList($input: UserStatusListInput) {
    userStatusList(input: $input) {
      users {
        id
        created
        modified
        status
        fName
        lName
        email
        groups
        scopes
        _optimistic @client
      }
      invites {
        id
        created
        modified
        status
        email
        _optimistic @client
      }
      usersNextToken
      invitesNextToken
    }
  }
`;

export const GET_ORGANISATION = gql`
  query getOrganisation($id: String!) {
    organisation(orgId: $id) {
      defaultRoute
      advancedRouting {
        send {
          followUpAction
          followUpRoute
        }
        receive {
          followUpAction
          followUpRoute
        }
      }
      legalEntity {
        address {
          city
          country
          line1
          line2
          postalCode
          state
        }
        taxId
        taxIdType
        taxRepresentative {
          name
          country
          taxId
          address {
            city
            country
            state
            postalCode
            line2
            line1
          }
        }
      }
      access {
        scopes
        sourceIps
        tokenIds
      }
      plan
      country
      currency
      notificationEmails
      peppolId
      id
      parentId
      name
      status
      smartParse {
        destinationId
      }
      are {
        contact {
          name
          phone
          email
        }
        legalRegistration {
          agencyId
          agencyName
        }
      }
      uaeLinking {
        tin
        vatTrn
        peppolParticipantId
        entityNameEn
        entityNameAr
        effectiveDate
        legalType
        status
        verifiedAt
        lastEventAt
        offboardedAt
        offboardReason
      }
      chl {
        taxIdType
      }
      den {
        schemeIds {
          client
          legalEntity
          company
        }
        contact {
          email
        }
        vatNumber
        endPointId
      }
      deu {
        contact {
          name
          email
          phone
        }
        taxRepresentative {
          name
          taxId
          country
        }
      }
      egy {
        onboarded
        sftp {
          host
          username
          password
          port
        }
      }
      emailSettings {
        brand {
          color
        }
        replyTo
        cc
        bcc
        sendToRecipient
        sendToSender
        emailNotes
      }
      fra {
        onboarded
        profileType
        senderSiren
      }
      grc {
        onboarded
        auth {
          key
        }
        series
      }
      hun {
        onboarded
        contact {
          name
          phone
          email
        }
        vatCode
        bankAccount {
          number
        }
        vatGroup {
          taxId
          vatCode
          country
        }
        auth {
          username
          password
          xmlSignatureKey
          xmlExchangeKey
        }
      }
      hrv {
        onboarded
        auth {
          apiKey
        }
        contact {
          name
          email
          phone
        }
        electronicAddress {
          schemeId
          endpointId
          authority
        }
        oib
      }
      ita {
        contact {
          email
          phone
        }
        issuer
        issuingSystem
        onboarded
        taxIdType
        taxRegime
      }
      jor {
        onboarded
        auth {
          orgKey
          apiKey
          channel
        }
      }
      jpn {
        electronicAddress {
          schemeId
          endpointId
        }
      }
      ind
      ken {
        onboarded
        auth {
          apiKey
        }
      }
      kor {
        onboarded
        auth {
          secretKey
          userId
          linkId
          corpNum
        }
        businessRegistrationNum
        taxRegID
        representativeName
      }
      mex {
        onboarded
        taxIdType
      }
      mock {
        email
      }
      mus {
        auth {
          username
          password
          ebsMraId
        }
        tan
        brn
        ebsCounterNo
        cashierId
        businessPhoneNo
      }
      mys {
        onboarded
        clientId
        clientSecret
        clientIdFallback
        clientSecretFallback
        sstRegistrationNumber
        tourismTaxRef
        industryClassificationCode
        businessActivity
        brnNumber
        contact {
          phone
        }
      }
      nemHandel {
        id
      }
      nga {
        onboarded
        auth {
          apiKey
        }
        contact {
          phone
          email
        }
      }
      pak {
        auth {
          userName
          password
          apiKey
          webDnaCode
        }
      }
      pol {
        onboarded
        auth {
          x509Certificate
          privateKey
          offlineCertificate
          offlinePrivateKey
          ksefToken
        }
        contact {
          phone
          email
        }
      }
      ppl {
        # companyId
        electronicAddress {
          schemeId
          endpointId
        }
        receiveOnPeppol
      }
      prt {
        onboarded
        invoiceSeriesName
        simplifiedInvoiceSeriesName
        creditNoteSeriesName
      }
      rou
      sapConcur {
        onboarded
        scope
        tokenType
        refreshToken
        refreshExpiresIn
        geolocation
        policyId
        ledgerCode
        customInfo
      }
      stripe {
        accountId
      }
      sau {
        onboarded
        certs {
          crt
          privateKey
          compliance {
            requestId
            token
            tokenType
            secret
          }
          production {
            requestId
            token
            tokenType
            secret
          }
        }
      }
      tur {
        onboarded
        auth {
          username
          password
        }
        taxOffice
      }
      twn {
        contact {
          name
          phone
          email
          fax
        }
        username
        password
        companyKey
      }
      yokoy {
        onboarded
        organisationRef
        legalEntityRef
        customInfo
        clientId
        clientSecret
      }
    }
  }
`;

export const GET_ORGANISATIONS = gql`
  query getOrganisations($userId: String) {
    organisations(userId: $userId) {
      access {
        scopes
        groups
      }
      userId
      orgId
      name
      status
      org {
        legalEntity {
          address {
            city
            country
            line1
            line2
            postalCode
            state
          }
        }
        defaultRoute
      }
    }
  }
`;

export const GET_INVOICE = gql`
  query getInvoice($orgId: String!, $invoiceId: String!) {
    getInvoice(orgId: $orgId, invoiceId: $invoiceId) {
      created
      id
      issued
      modified
      dueDate
      supplyDate
      supplyEndDate
      orgId
      parentOrgId
      isSelfBilled
      recipient {
        address {
          city
          country
          line1
          line2
          postalCode
          state
        }
        contact {
          email
          phone
        }
        taxRepresentative {
          name
          taxId
          address {
            city
            country
            line1
            line2
            postalCode
            state
          }
        }
        customInfo
        name
        # TODO: Remove in favor of customInfo.peppolId
        peppolId {
          endpointId
        }
        taxId
        taxScheme
      }
      sender {
        address {
          city
          country
          line1
          postalCode
          state
        }
        name
        taxId
        taxRepresentative {
          name
          taxId
          address {
            city
            country
            line1
            line2
            postalCode
            state
          }
        }
      }
      shipToParty {
        name
        taxId
        address {
          line1
          line2
          city
          state
          postalCode
          country
        }
      }
      paymentMeans {
        code
        ref
        account {
          iban
          bic
          accountName
          accountNumber
          bankName
          branch
        }
        card {
          pan
          network
          holderName
        }
        directDebit {
          id
          schemeId
        }
        provider {
          name
          scheme
          url
          ref
        }
        other
      }
      ref
      routeRef
      route
      salesOrderRef
      purchaseOrderRef
      status
      type
      total {
        amount
        currency
      }
      lines
      customInfo {
        chn
        deu
        esp
        exchangeRate
        fra
        grc
        hun
        ita
        ind
        ken
        taIND {
          SignedQRCode
        }
        mex {
          docUrl
          reasonCode
          typeCode
          recipientTaxIdType
          recipientIndustryClassificationCode
        }
        mus
        taMUS
        mys
        nga
        pak
        peppol
        pol
        prt
        rou
        sau
        tur
        taTWN
        twn
      }
      attachments {
        name
        hash
        contentType
        contentLength
        type
        url
      }
      notes
      error {
        description
        name
        message
      }
    }
  }
`;

export const GET_INVOICES = gql`
  query invoiceList($skip: Int, $limit: Int, $orgId: String, $q: String, $sortOn: String, $sortDir: String) {
    invoiceList(skip: $skip, limit: $limit, orgId: $orgId, q: $q, sortOn: $sortOn, sortDir: $sortDir) {
      total
      items {
        created
        id
        issued
        modified
        orgId
        parentOrgId
        recipient {
          address {
            city
            country
            line1
            postalCode
            state
          }
          name
        }
        sender {
          address {
            city
            country
            line1
            postalCode
            state
          }
          name
        }
        ref
        route
        status
        type
        total {
          amount
          currency
        }
        customInfo {
          mex {
            docUrl
          }
          prt
        }
        error {
          description
          name
          message
        }
      }
    }
  }
`;

export const GET_CSV_INVOICES = gql`
  query invoiceList($skip: Int, $limit: Int, $orgId: String, $q: String, $sortOn: String, $sortDir: String) {
    invoiceList(skip: $skip, limit: $limit, orgId: $orgId, q: $q, sortOn: $sortOn, sortDir: $sortDir) {
      total
      items {
        id
        source
      }
    }
  }
`;

export const GET_CHART = gql`
  query getChart($orgId: String, $interval: String, $type: String, $docType: String!, $currency: String) {
    getChart(orgId: $orgId, interval: $interval, type: $type, docType: $docType, currency: $currency) {
      results
    }
  }
`;

export const GET_INVOICE_STATS = gql`
  query invoiceStats($orgId: String, $docType: String) {
    invoiceStats(orgId: $orgId, docType: $docType)
  }
`;

export const GET_CSV_CREDIT_NOTE_LIST = gql`
  query creditNoteList($orgId: String, $limit: Int, $q: String, $skip: Int, $sortOn: String, $sortDir: String) {
    creditNoteList(orgId: $orgId, limit: $limit, q: $q, skip: $skip, sortOn: $sortOn, sortDir: $sortDir) {
      total
      items {
        id
        source
      }
    }
  }
`;

export const GET_CREDIT_NOTE_LIST = gql`
  query creditNoteList($orgId: String, $limit: Int, $q: String, $skip: Int, $sortOn: String, $sortDir: String) {
    creditNoteList(orgId: $orgId, limit: $limit, q: $q, skip: $skip, sortOn: $sortOn, sortDir: $sortDir) {
      total
      items {
        created
        id
        invoiceRefs
        status
        ref
        route
        recipient {
          name
          taxId
        }
        sender {
          name
        }
        type
        total {
          amount
          currency
        }
        orgId
        parentOrgId
        modified
        issued
        error {
          description
          name
          message
        }
      }
    }
  }
`;

export const GET_CREDIT_NOTE = gql`
  query getCreditNote($creditNoteId: String!, $orgId: String!) {
    getCreditNote(creditNoteId: $creditNoteId, orgId: $orgId) {
      id
      created
      invoiceRefs
      issued
      modified
      dueDate
      supplyDate
      supplyEndDate
      isSelfBilled
      customInfo {
        chn
        deu
        esp
        exchangeRate
        fra
        grc
        hun
        ita
        ind
        ken
        taIND {
          SignedQRCode
        }
        mex {
          docUrl
          reasonCode
          typeCode
          recipientTaxIdType
          recipientIndustryClassificationCode
        }
        mus
        taMUS
        mys
        nga
        pak
        peppol
        pol
        prt
        rou
        sau
        tur
        taTWN
        twn
      }
      lines
      total {
        amount
        currency
      }
      status
      ref
      routeRef
      recipient {
        name
        taxId
        taxScheme
        customInfo
        # TODO: Remove in favor of customInfo.peppolId
        peppolId {
          endpointId
        }
        address {
          city
          country
          line1
          line2
          postalCode
          state
        }
        contact {
          email
          phone
        }
        taxRepresentative {
          name
          taxId
          address {
            country
          }
        }
      }
      sender {
        name
        taxId
        address {
          city
          country
          line1
          line2
          postalCode
          state
        }
        taxRepresentative {
          name
          taxId
          address {
            country
          }
        }
      }
      shipToParty {
        name
        taxId
        address {
          line1
          line2
          city
          state
          postalCode
          country
        }
      }
      paymentMeans {
        code
        ref
        account {
          iban
          bic
          accountName
          accountNumber
          bankName
          branch
        }
        card {
          pan
          network
          holderName
        }
        directDebit {
          id
          schemeId
        }
        provider {
          name
          scheme
          url
          ref
        }
        other
      }
      ref
      routeRef
      route
      salesOrderRef
      purchaseOrderRef
      orgId
      parentOrgId
      attachments {
        name
        hash
        contentType
        contentLength
        type
        url
      }
      notes
      error {
        description
        name
        message
      }
    }
  }
`;

export const GET_BULK_ATTACHMENTS = gql`
  query getBulkAttachments(
    $skip: Int
    $limit: Int
    $orgId: String!
    $q: String
    $sortOn: String
    $sortDir: String
    $docType: String!
  ) {
    getBulkAttachments(
      skip: $skip
      limit: $limit
      orgId: $orgId
      q: $q
      sortOn: $sortOn
      sortDir: $sortDir
      docType: $docType
    ) {
      url
    }
  }
`;

// Define the GraphQL query for getting an attachment URL
export const GET_ATTACHMENT_URL = gql`
  query getAttachmentUrl($orgId: String!, $docId: String!, $hash: String!, $docType: String!) {
    getAttachmentUrl(orgId: $orgId, docId: $docId, hash: $hash, docType: $docType) {
      url
    }
  }
`;

export const INTERPRET_ERROR = gql`
  query interpretError($orgId: String!, $errorText: String!) {
    interpretError(orgId: $orgId, errorText: $errorText) {
      interpretation
    }
  }
`;

export const GET_ORG_LIST = gql`
  query orgList($skip: Int, $limit: Int, $q: String) {
    orgList(skip: $skip, limit: $limit, q: $q) {
      total
      items {
        id
        name
        status
        plan
        legalEntity {
          taxId
          taxIdType
        }
        # Route configurations
        chl {
          taxIdType
        }
        den {
          schemeIds {
            client
            legalEntity
            company
          }
        }
        deu {
          contact {
            name
            email
          }
        }
        egy {
          onboarded
          sftp {
            host
            username
            password
            port
          }
        }
        fra {
          onboarded
          profileType
          senderSiren
        }
        grc {
          onboarded
        }
        hun {
          onboarded
        }
        hrv {
          onboarded
        }
        ind
        ita {
          onboarded
        }
        jor {
          onboarded
        }
        jpn {
          electronicAddress {
            schemeId
          }
        }
        ken {
          onboarded
        }
        kor {
          onboarded
          auth {
            secretKey
            userId
            linkId
            corpNum
          }
          businessRegistrationNum
          taxRegID
          representativeName
        }
        mex {
          onboarded
        }
        mys {
          sstRegistrationNumber
        }
        nemHandel {
          id
        }
        pol {
          onboarded
        }
        ppl {
          electronicAddress {
            schemeId
          }
        }
        prt {
          onboarded
        }
        rou
        sapConcur {
          onboarded
        }
        sau {
          onboarded
        }
        twn {
          contact {
            name
          }
        }
        yokoy {
          onboarded
        }
        taxId
        routes
      }
    }
  }
`;

export const SMART_PARSE_JOB_LIST = gql`
  query smartParseJobList($orgId: String!) {
    smartParseJobList(orgId: $orgId) {
      items {
        id
        organisationId
        status
        created
        modified
        filename
        size
        meta {
          started
          lines
          errors {
            total
          }
          skipped {
            total
          }
          success {
            total
          }
        }
      }
      total
    }
  }
`;
export const JOB_FILE_DONWLOAD = gql`
  query smartParseGetFileLink($orgId: String!, $jobId: String!, $status: String!) {
    smartParseGetFileLink(orgId: $orgId, customInfo: { smartParse: { jobId: $jobId, status: $status } }) {
      url
    }
  }
`;

export const LIST_JWT_TOKENS = gql`
  query listJwtTokens($orgId: String!) {
    listJwtTokens(orgId: $orgId) {
      tokens {
        type
        tokenId
        name
        maskedValue
        createdAt
        createdBy
        expiresAt
      }
    }
  }
`;
